import { useContext, useEffect, useState } from 'react'
import Header from './Header'
import Content from '../../components/Content'
import Padded from '../../components/Padded'
import FlexCol from '../../components/FlexCol'
import Text, { TextSecondary } from '../../components/Text'
import Button from '../../components/Button'
import { WalletContext } from '../../providers/wallet'
import { ConfigContext } from '../../providers/config'
import { AspContext } from '../../providers/asp'
import { getReceivingAddresses } from '../../lib/asp'
import { Addresses } from '../../lib/types'
import { getWebExplorerURL } from '../../lib/explorers'
import { buildVersion, sdkVersion, NetworkName } from '@arkade-os/sdk'
import ChatwootWidget from '../../components/ChatWoot'
import ButtonsOnBottom from '../../components/ButtonsOnBottom'
import ErrorMessage from '../../components/Error'
import { hasChatwootVars, setChatwootLocale, getChatwootLocale } from '../../lib/chatwoot'
import { getDefaultAddress } from '../../lib/address'
import { gitCommit } from '../../_gitCommit'
import { useTranslation } from '../../providers/language'

export default function Support() {
  const { aspInfo } = useContext(AspContext)
  const { config } = useContext(ConfigContext)
  const { wallet, svcWallet } = useContext(WalletContext)
  const { language, t } = useTranslation()

  const [error, setError] = useState('')
  const [addresses, setAddresses] = useState<Addresses>()
  const [supportChatLoaded, setSupportChatLoaded] = useState(false)

  // Fetch wallet addresses
  useEffect(() => {
    if (svcWallet) {
      getReceivingAddresses(svcWallet)
        .then(setAddresses)
        .catch((err) => console.error('Failed to get addresses:', err))
    }
  }, [svcWallet])

  // Wait for Chatwoot to load, show error after 5 seconds if not loaded
  useEffect(() => {
    // If Chatwoot is already loaded, set state immediately
    if (window.$chatwoot) {
      window.$chatwoot?.toggleBubbleVisibility('hide')
      setSupportChatLoaded(true)
      return
    }

    // Not all networks may have Chatwoot configured, check for required vars before waiting
    if (!hasChatwootVars()) return setError(t('settings.supportChatNotConfigured'))

    // Timeout to detect if Chatwoot fails to load
    const loadTimeout = setTimeout(() => {
      if (!supportChatLoaded) setError(t('settings.failedToLoadSupportChat'))
    }, 5_000)

    // Listen for Chatwoot ready event to set loaded state
    const eventHandler = () => {
      clearTimeout(loadTimeout)
      setSupportChatLoaded(true)
      window.$chatwoot?.toggleBubbleVisibility('hide')
    }

    const event = 'chatwoot:ready'
    window.addEventListener(event, eventHandler)

    return () => {
      clearTimeout(loadTimeout)
      window.removeEventListener(event, eventHandler)
    }
  }, [t])

  // Keep the Chatwoot widget UI in the active wallet language. The locale is
  // also set at injection time (see ChatWoot.tsx); this covers mid-session
  // language switches while the widget is already loaded.
  useEffect(() => {
    if (!window.$chatwoot) return
    setChatwootLocale(getChatwootLocale(language))
  }, [language])

  // Set Chatwoot user and custom attributes when addresses are available
  useEffect(() => {
    if (!addresses || !window.$chatwoot || !wallet.pubkey) return

    // Set user identifier (using wallet pubkey)
    const userIdentifier = wallet.pubkey.substring(0, 16)
    window.$chatwoot.setUser(userIdentifier, { name: `User ${userIdentifier}` })

    const defaultAddress = getDefaultAddress(wallet.pubkey, aspInfo)

    // Set custom attributes including addresses and service URLs
    window.$chatwoot.setCustomAttributes({
      wallet_pubkey: wallet.pubkey,
      network: wallet.network || 'not available',
      location_origin: window.location.origin,
      default_address: defaultAddress,
      ark_address: addresses.offchainAddr || 'not available',
      indexer_url: aspInfo.url || config.aspUrl || 'not available',
      btc_boarding_address: addresses.boardingAddr || 'not available',
      ark_server_url: aspInfo.url || config.aspUrl || 'not available',
      app_version: import.meta.env.VITE_APP_VERSION || 'not available',
      explorer_url: wallet.network ? getWebExplorerURL(wallet.network as NetworkName) : 'not available',
      build_version: buildVersion,
      sdk_version: sdkVersion,
      git_commit: gitCommit,
    })
  }, [addresses, wallet.pubkey, supportChatLoaded])

  const handleOpenChat = () => {
    if (window.$chatwoot) window.$chatwoot.toggle('open')
  }

  const Section = ({ title, text }: { title: string; text: string }) => (
    <FlexCol gap='0.5rem'>
      <Text thin>{title}</Text>
      <TextSecondary>{text}</TextSecondary>
    </FlexCol>
  )

  return (
    <>
      <Header text={t('settings.support')} back />
      <Content>
        <Padded>
          <FlexCol gap='1rem'>
            <ErrorMessage error={Boolean(error)} text={error} />
            <Section title={t('settings.customerSupport')} text={t('settings.customerSupportText')} />
            <Section title={t('settings.secureChat')} text={t('settings.secureChatText')} />
            <Section title={t('settings.bugReports')} text={t('settings.bugReportsText')} />
            <Section title={t('settings.trackProgress')} text={t('settings.trackProgressText')} />
            <ChatwootWidget />
          </FlexCol>
        </Padded>
      </Content>
      <ButtonsOnBottom>
        {error ? null : (
          <Button
            onClick={handleOpenChat}
            disabled={!supportChatLoaded}
            label={supportChatLoaded ? t('settings.openSupportChat') : t('common.loading')}
          />
        )}
      </ButtonsOnBottom>
    </>
  )
}
